// components/SuggestionForm.jsx
import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useLocation } from "react-router-dom";

import useGraphQLClient from "../hooks/useGraphQLClient";
import { useTenant } from "../context/TenantContext";
import { useAuth } from "../auth/AuthContext.jsx";
import { CREATE_TASK } from "../graph/tasks/mutations";
import { useSnackbar } from "../components/snackbarProvider.jsx";
import MarkdownEditor from "../components/MarkdownEditor";

const suggestionSchema = yup.object().shape({
  description: yup.string().nullable(),
  feedbackType: yup.string().required("Type is required"),
});

const initialValues = {
  description: "",
  feedbackType: "suggestion",
};

const formatFeedbackType = (feedbackType) =>
  feedbackType.charAt(0).toUpperCase() + feedbackType.slice(1);

const SuggestionForm = ({ onSuccess }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();
  const { user } = useAuth();
  const location = useLocation();

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      if (!tenantId) {
        setSnackbar({
          open: true,
          severity: "warning",
          message: "Please select a tenant before submitting feedback",
        });
        return;
      }

      const feedbackTypeLabel = formatFeedbackType(values.feedbackType);

      const submittedDate = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

      const submittedAt = new Date().toISOString();

      const userName = user
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
        : "Unknown user";

      const userEmail = user?.email || "Unknown email";
      const currentUrl = window.location.href;
      const browser = navigator.userAgent;

      const generatedTitle = `${feedbackTypeLabel} - ${submittedDate}`;

      const enrichedDescription = `
## Feedback

${values.description?.trim() || "_No description provided._"}

---

## Submitted By

| Field | Value |
|---|---|
| Name | ${userName || "Unknown user"} |
| Email | ${userEmail} |
| Tenant | ${tenantName || "Unknown tenant"} |
| Tenant ID | ${tenantId} |
| Type | ${feedbackTypeLabel} |
| Page | ${location.pathname} |
| URL | ${currentUrl} |
| Browser | ${browser} |
| Submitted At | ${submittedAt} |
`.trim();

      const input = {
        title: generatedTitle,
        description: enrichedDescription,
        tenantId,
        status: "INBOX",
        labels: ["feedback"],
        projectId: null,
      };

      await client.request(CREATE_TASK, { input });

      resetForm();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Feedback submitted - thanks!",
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to submit feedback",
      });
    }
  };

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={suggestionSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box
            display="grid"
            gap="30px"
            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            sx={{
              "& > div": {
                gridColumn: isNonMobile ? undefined : "span 4",
              },
            }}
          >
            <FormControl fullWidth sx={{ gridColumn: "span 4" }}>
              <InputLabel>Type</InputLabel>
              <Select
                name="feedbackType"
                value={values.feedbackType}
                label="Type"
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <MenuItem value="suggestion">Suggestion</MenuItem>
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="question">Question</MenuItem>
              </Select>
            </FormControl>

            <MarkdownEditor
              value={values.description}
              onChange={(nextValue) =>
                setFieldValue("description", nextValue)
              }
              label="Describe your feedback"
              minRows={10}
              error={!!touched.description && !!errors.description}
              helperText={touched.description && errors.description}
              sx={{ gridColumn: "span 4" }}
            />
          </Box>

          <Box display="flex" justifyContent="end" mt="20px">
            <Button type="submit" color="primary" variant="contained">
              Submit Feedback
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default SuggestionForm;